import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import logger from '../../../utils/logger';

/**
 * Emoji reaction handler with optimistic override semantics.
 *
 * State model:
 *   - `serverUserEmoji`: the viewer's current emoji according to the latest
 *     fetched song document.
 *   - `emojiOverride`: optimistic value applied locally while a mutation is
 *     in flight. `undefined` means "follow server"; `null` means "viewer
 *     has cleared their reaction"; any string is the pending emoji.
 *
 * Effect clears the override as soon as the server catches up to avoid a
 * stale display once the next song fetch reconciles things.
 *
 * `displayEmojiReactions` rebuilds the full reactions array with the
 * override swapped in so the aggregate counts match what the viewer just
 * tapped, immediately.
 */
const useSongEmojiActions = ({
  songDetails,
  setSongDetails,
  currentUser,
  addEmojiReaction,
  removeEmojiReaction,
  getSongById,
}) => {
  const [emojiOverride, setEmojiOverride] = useState(undefined);

  const serverUserEmoji = useMemo(() => {
    if (!currentUser || !songDetails?.emojiReactions) return null;
    const userReaction = songDetails.emojiReactions.find(
      (reaction) => reaction.userId === currentUser.uid
    );
    return userReaction ? userReaction.emoji : null;
  }, [currentUser, songDetails?.emojiReactions]);

  const displayUserEmoji =
    emojiOverride !== undefined ? emojiOverride : serverUserEmoji;

  const displayEmojiReactions = useMemo(() => {
    const base = songDetails?.emojiReactions || [];
    if (emojiOverride === undefined) return base;
    const withoutUser = base.filter((r) => r.userId !== currentUser?.uid);
    if (emojiOverride === null) return withoutUser;
    return [...withoutUser, { userId: currentUser.uid, emoji: emojiOverride }];
  }, [songDetails?.emojiReactions, emojiOverride, currentUser?.uid]);

  useEffect(() => {
    if (emojiOverride === undefined) return;
    if (serverUserEmoji === emojiOverride) {
      setEmojiOverride(undefined);
    }
  }, [serverUserEmoji, emojiOverride]);

  useLayoutEffect(() => {
    setEmojiOverride(undefined);
  }, [songDetails?.id]);

  const resetOverrideOnSongChange = useCallback(() => {
    setEmojiOverride(undefined);
  }, []);

  const handleEmojiSelect = useCallback(
    async (emoji) => {
      if (!currentUser) return;

      const existingReaction = songDetails.emojiReactions?.find(
        (reaction) => reaction.userId === currentUser.uid
      );

      const intendedEmoji =
        existingReaction && existingReaction.emoji === emoji ? null : emoji;
      setEmojiOverride(intendedEmoji);

      try {
        if (existingReaction) {
          if (existingReaction.emoji === emoji) {
            await removeEmojiReaction(songDetails.id, currentUser.uid);
          } else {
            await addEmojiReaction(songDetails.id, emoji, currentUser.uid);
          }
        } else {
          await addEmojiReaction(songDetails.id, emoji, currentUser.uid);
        }

        const updatedSong = await getSongById(songDetails.id);
        setSongDetails(updatedSong);
      } catch (error) {
        logger.error('Error handling emoji selection:', error);
        setEmojiOverride(undefined);
      }
    },
    [
      currentUser,
      songDetails,
      addEmojiReaction,
      removeEmojiReaction,
      getSongById,
      setSongDetails,
    ]
  );

  return {
    handleEmojiSelect,
    displayUserEmoji,
    displayEmojiReactions,
    resetOverrideOnSongChange,
  };
};

export default useSongEmojiActions;
